/**
 * GenerationAlert Component
 * Modal component for handling missing authorization key scenarios and generation feedback
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Settings, X } from 'lucide-react';

export interface GenerationAlertProps {
    isVisible: boolean;
    type: 'missing-key' | 'success' | 'error';
    title?: string;
    message?: string;
    onClose: () => void;
    onNavigateToSettings?: () => void;
}

const GenerationAlert: React.FC<GenerationAlertProps> = ({
    isVisible,
    type,
    title,
    message,
    onClose,
    onNavigateToSettings
}) => {
    const navigate = useNavigate();

    if (!isVisible) {
        return null;
    }

    const handleNavigateToSettings = () => {
        if (onNavigateToSettings) {
            onNavigateToSettings();
        } else {
            navigate('/settings');
        }
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
        }
    };

    const getAlertContent = () => {
        switch (type) {
            case 'missing-key':
                return {
                    icon: <AlertTriangle size={48} className="alert-icon warning" />,
                    title: title || 'Authorization Required',
                    message: message || 'You need to configure an authorization key before generating videos.',
                    primaryAction: {
                        label: 'Go to Settings',
                        onClick: handleNavigateToSettings,
                        icon: <Settings size={18} />
                    },
                    secondaryAction: {
                        label: 'Cancel',
                        onClick: onClose
                    }
                };
            case 'success':
                return {
                    icon: <CheckCircle size={48} className="alert-icon success" />,
                    title: title || 'Generation Started!',
                    message: message || 'Your video generation has been submitted successfully.',
                    primaryAction: {
                        label: 'OK',
                        onClick: onClose
                    }
                };
            case 'error':
                return {
                    icon: <AlertTriangle size={48} className="alert-icon error" />,
                    title: title || 'Generation Failed',
                    message: message || 'There was an error generating your video. Please try again.',
                    primaryAction: {
                        label: 'OK',
                        onClick: onClose
                    }
                };
            default:
                return {
                    icon: <AlertTriangle size={48} className="alert-icon" />,
                    title: title || 'Alert',
                    message: message || '',
                    primaryAction: {
                        label: 'OK',
                        onClick: onClose
                    }
                };
        }
    };

    const alertContent = getAlertContent();

    return (
        <div
            className="modal-overlay generation-alert-overlay"
            onClick={onClose}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div
                className="modal generation-alert-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="alert-title"
                aria-describedby="alert-message"
            >
                <div className="modal-header generation-alert-header">
                    <div className="alert-icon-container">
                        {alertContent.icon}
                    </div>
                    <button
                        onClick={onClose}
                        className="btn-icon alert-close-btn"
                        aria-label="Close alert"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body generation-alert-body">
                    <h3 id="alert-title" className="alert-title">
                        {alertContent.title}
                    </h3>
                    <p id="alert-message" className="alert-message">
                        {alertContent.message}
                    </p>
                </div>

                <div className="modal-footer generation-alert-footer">
                    <button
                        onClick={alertContent.primaryAction.onClick}
                        className="btn-primary alert-primary-btn"
                        autoFocus
                    >
                        {alertContent.primaryAction.icon && alertContent.primaryAction.icon}
                        <span>{alertContent.primaryAction.label}</span>
                    </button>

                    {alertContent.secondaryAction && (
                        <button
                            onClick={alertContent.secondaryAction.onClick}
                            className="btn-secondary alert-secondary-btn"
                        >
                            <span>{alertContent.secondaryAction.label}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GenerationAlert;
